import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Article as ArticleIcon,
  Person as PersonIcon,
  Science as ScienceIcon,
  HelpOutline as ProblemIcon,
} from '@mui/icons-material';
import { NODE_COLORS } from './Graph/tokens';
import { entityHref, kindOfNode, kindMeta } from './entity/entityKinds';
import { useCollections } from '../contexts/CollectionContext';
import { refineGroups } from './searchResults';
import Kicker from './ui/Kicker';

/**
 * GlobalSearch
 * ------------
 * Ctrl+K (or Cmd+K) command palette.
 *
 * - Calls `/api/search/autocomplete?q=...&grouped=true` — results come back
 *   bucketed by category (Papers, Methods, Authors, …) HF-style, each row with
 *   quick actions (Digest / Graph / …). Falls back to the flat list, then to
 *   client-side graph filtering if the endpoint is unavailable.
 * - Keyboard: Esc closes, Enter triggers the row's primary action, Up/Down move.
 */

const TYPE_META = {
  method: { icon: ScienceIcon, color: NODE_COLORS.method, label: 'Method' },
  author: { icon: PersonIcon, color: NODE_COLORS.author, label: 'Author' },
  problem: { icon: ProblemIcon, color: NODE_COLORS.problem, label: 'Problem' },
  dataset: { icon: ScienceIcon, color: NODE_COLORS.dataset, label: 'Dataset' },
  task: { icon: ScienceIcon, color: NODE_COLORS.task, label: 'Task' },
  paper: { icon: ArticleIcon, color: NODE_COLORS.consolidating, label: 'Paper' },
  article: { icon: ArticleIcon, color: NODE_COLORS.consolidating, label: 'Paper' },
};

const CATEGORY_LABELS = {
  paper: 'Papers', method: 'Methods', author: 'Authors', dataset: 'Datasets',
  task: 'Tasks', problem: 'Problems', topic: 'Topics', component: 'Components',
  technique: 'Techniques', reference: 'References',
};

function getApiBase() {
  // Prefer relative URL so nginx proxy handles it in prod.
  return '';
}

function authHeader() {
  try {
    const token = localStorage.getItem('access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (_) {
    return {};
  }
}

// Per-item quick actions (HF-style). Returns [{label, to}], first = primary.
function quickActionsFor(item) {
  const t = (item.type || '').toLowerCase();
  const id = encodeURIComponent(item.id);
  if (t === 'paper' || t === 'article') {
    const acts = [];
    if (!item.status || item.status === 'completed') {
      acts.push({ label: 'Digest', to: `/digest/${item.id}` });
      acts.push({ label: 'Paper', to: `/digest/${item.id}?view=paper` });
    }
    acts.push({ label: 'Graph', to: `/graph?focus=m_${item.id}&view=fusion` });
    return acts.length ? acts : [{ label: 'Open', to: `/articles?focus=${id}` }];
  }
  if (t === 'author' || t === 'authors') {
    return [
      { label: 'Profile', to: `/author-insights/author/${id}` },
      { label: 'Page', to: entityHref('author', item.id, { label: item.name || item.label }) },
    ];
  }
  // Any other vertex: its entity page first (story + ranked papers), the graph second.
  const kind = ['methods', 'breakthrough', 'consolidating', 'innovative', 'survey'].includes(t)
    ? 'method'
    : kindOfNode({ ...item, type: t.replace(/s$/, '') });
  const page = kind ? entityHref(kind, item.id, { label: item.name || item.label || item.title }) : null;
  const graph = { label: 'Graph', to: `/graph?focus=${id}&view=${kind ? kindMeta(kind).view : 'fusion'}` };
  return page ? [{ label: 'Page', to: page }, graph] : [graph];
}

export default function GlobalSearch({ open, onClose }) {
  const [query, setQuery] = useState('');
  // groups: [{category, label, items:[...]}]; flat: the same items in one array
  // (index-aligned with keyboard selection).
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const onGraphPage = location.pathname.startsWith('/graph');
  // Search the collection the user is working in, like every other page does,
  // and say so: a paper outside it is not missing, it is out of scope — one
  // click widens the search to every collection.
  const { selectedCollection, collections } = useCollections() || {};
  const activeCollectionId = selectedCollection && selectedCollection !== 'all' ? String(selectedCollection) : 'all';
  const [widened, setWidened] = useState(false);
  const collectionId = widened ? 'all' : activeCollectionId;
  const scopeName = activeCollectionId === 'all'
    ? null
    : ((collections || []).find((c) => String(c?.id) === activeCollectionId)?.name || `collection ${activeCollectionId}`);

  const flat = useMemo(() => groups.flatMap((g) => g.items), [groups]);

  // Reset state every time the dialog opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setGroups([]);
      setSelectedIndex(0);
      setWidened(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Fallback fetch: graph data filtered client-side → one "Results" group.
  const fallbackFetch = useCallback(async (q) => {
    const lower = q.toLowerCase();
    const res = await axios.get(`${getApiBase()}/api/graph/data`, {
      params: { view: 'fusion', limit: 200, collection_id: collectionId },
      headers: authHeader(),
    });
    const nodes = res?.data?.nodes || [];
    return nodes
      .filter((n) => (n.label || n.name || n.id || '').toString().toLowerCase().includes(lower))
      .slice(0, 20)
      .map((n) => ({
        id: n.id,
        label: n.label || n.name || n.id,
        type: (n.ui_type || n.type || 'unknown').toString().toLowerCase(),
        subtitle: n.description || '',
      }));
  }, [collectionId]);

  // Group a flat list by `type` into our category buckets (used for the
  // back-compat flat path + fallback).
  const groupFlat = useCallback((items) => {
    const byCat = {};
    items.forEach((it) => {
      const t = (it.type || '').toLowerCase();
      const cat = ['method', 'breakthrough', 'consolidating', 'innovative', 'survey'].includes(t) ? 'method'
        : ['paper', 'article'].includes(t) ? 'paper'
        : ['author', 'dataset', 'task', 'problem', 'component', 'technique', 'reference'].includes(t) ? t
        : 'topic';
      (byCat[cat] = byCat[cat] || []).push(it);
    });
    const order = ['paper', 'method', 'author', 'dataset', 'task', 'problem', 'topic', 'component', 'technique', 'reference'];
    return order.filter((c) => byCat[c]).map((c) => ({
      category: c, label: CATEGORY_LABELS[c] || c, items: byCat[c],
    }));
  }, []);

  // Run a search whenever query changes (with debounce)
  useEffect(() => {
    if (!open) return undefined;
    if (!query || query.trim().length < 2) {
      setGroups([]);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        let nextGroups = null;
        try {
          const res = await axios.get(`${getApiBase()}/api/search/autocomplete`, {
            params: { q: query, grouped: true, limit: 8, collection_id: collectionId },
            headers: authHeader(),
            timeout: 5000,
          });
          if (Array.isArray(res?.data?.groups)) {
            nextGroups = res.data.groups;
          } else if (Array.isArray(res?.data?.results)) {
            nextGroups = groupFlat(res.data.results);
          } else if (Array.isArray(res?.data)) {
            nextGroups = groupFlat(res.data);
          }
        } catch (e) {
          nextGroups = groupFlat(await fallbackFetch(query));
        }
        if (!cancelled) {
          setGroups(refineGroups(nextGroups || [], query));
          setSelectedIndex(0);
        }
      } catch (e) {
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query, open, fallbackFetch, groupFlat, collectionId]);

  const go = useCallback((to) => {
    if (!to) return;
    navigate(to);
    onClose?.();
  }, [navigate, onClose]);

  // Primary action for an item: on /graph prefer focusing; else the row's first action.
  const primaryFor = useCallback((item) => {
    if (!item) return null;
    const acts = quickActionsFor(item);
    if (onGraphPage) {
      const graphAct = acts.find((a) => a.label === 'Graph');
      if (graphAct) return graphAct.to;
    }
    return acts[0]?.to || null;
  }, [onGraphPage]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, Math.max(flat.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(primaryFor(flat[selectedIndex]));
    }
  };

  const placeholder = 'Search papers, methods, authors… (Ctrl+K)';
  const enterHint = 'Enter — open · ↑↓ to navigate · Esc to close';

  // Render: walk groups, keep a running flat index so selection highlight + ↑↓ align.
  let runningIndex = -1;

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { mt: { xs: 4, sm: 12 }, alignSelf: 'flex-start' } }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <TextField
            inputRef={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            fullWidth
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
              ) : null,
              sx: { fontSize: '1rem', py: 0.5 },
            }}
          />
          {scopeName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 0.75, pl: 4.5 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {widened ? 'Searching all collections' : `Searching ${scopeName}`}
              </Typography>
              <Button
                size="small"
                variant="text"
                onClick={() => { setWidened((w) => !w); inputRef.current?.focus(); }}
                sx={{ minWidth: 0, px: 0.75, py: 0, fontSize: '0.75rem', textTransform: 'none', flexShrink: 0 }}
              >
                {widened ? `Only ${scopeName}` : 'Search all collections'}
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
          {flat.length === 0 && !loading && query.trim().length >= 2 && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">
                No matches for “{query}”{scopeName && !widened ? ` in ${scopeName}` : ''}
              </Typography>
              {scopeName && !widened && (
                <Button size="small" onClick={() => setWidened(true)} sx={{ mt: 0.5, textTransform: 'none' }}>
                  Search all collections
                </Button>
              )}
            </Box>
          )}
          {flat.length === 0 && query.trim().length < 2 && (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Start typing to search papers and the knowledge graph</Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{enterHint}</Typography>
            </Box>
          )}

          {groups.map((group) => (
            <Box key={group.category}>
              <Kicker sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
                {group.label || CATEGORY_LABELS[group.category] || group.category}
              </Kicker>
              <List dense disablePadding>
                {group.items.map((item) => {
                  runningIndex += 1;
                  const idx = runningIndex;
                  const meta = TYPE_META[(item.type || '').toLowerCase()] || {
                    icon: SearchIcon, color: '#607d8b', label: item.type || '—',
                  };
                  const Icon = meta.icon;
                  const selected = idx === selectedIndex;
                  const actions = quickActionsFor(item);
                  return (
                    <ListItemButton
                      key={`${item.type}-${item.id}-${idx}`}
                      selected={selected}
                      onClick={() => go(primaryFor(item))}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      sx={{ py: 0.75, px: 2 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        <Box
                          sx={{
                            width: 26, height: 26, borderRadius: 1, display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: `${meta.color}1f`, color: meta.color, flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ fontSize: 15 }} />
                        </Box>
                        <ListItemText
                          primary={item.label || item.name}
                          secondary={item.subtitle || (item.year ? String(item.year) : null)}
                          primaryTypographyProps={{ fontSize: '0.88rem', fontWeight: 600, noWrap: true }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', noWrap: true }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          {actions.map((a) => (
                            <Button
                              key={a.label}
                              size="small"
                              variant="text"
                              onClick={() => go(a.to)}
                              sx={{ minWidth: 0, px: 0.75, py: 0.1, fontSize: '0.75rem', textTransform: 'none', color: 'primary.main' }}
                            >
                              {a.label}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          ))}
        </Box>

        {flat.length > 0 && (
          <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'rgba(0,0,0,0.02)' }}>
            <Typography variant="caption" color="text.secondary">{enterHint}</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
